import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Alert } from 'react-daisyui';
import Modal, { ModalProps } from '@/components/modals/Modal/Modal.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import useTripDelete from '@/api/trips/useTripDelete.ts';
import Routes from '@/router/Routes.ts';
import { useNavigate } from 'react-router-dom';
import useStorage from '@/api/useStorage.ts';
import StorageBucket from '$/_const/StorageBucket.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';

const DeleteTripModal: FC<ModalProps & { tripId: string }> = ({ onClose, tripId, ...modalProps }) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const { mutateAsync: deleteTrip, isPending: isLoadingDeleteTrip, error: errorDeleteTrip } = useTripDelete();
  const {
    remove: { mutateAsync: removeImage, isPending: isLoadingRemoveImage },
  } = useStorage(StorageBucket.TRIP_IMAGES);

  const handleConfirm = async () => {
    try {
      // TODO get path from trip not hardcode it
      await removeImage({ path: `${user?.id}/${tripId}.webp` });
      await deleteTrip([{ id: tripId }]);
      navigate(Routes.DASHBOARD);
      onClose?.();
    } catch (error) {
      console.error('Error deleting trip:', error);
    }
  };

  const isLoadingAction = isLoadingDeleteTrip || isLoadingRemoveImage;

  return (
    <Modal.Wrapper wrapperClassName="w-[400px]" {...modalProps} onClose={onClose} showCloseButton={false} closeable>
      <Modal.Title>
        <div className="flex flex-col gap-1 text-center">
          <h2 className="font-feature font-bold italic text-2xl">{t('deleteTripModal.title')}</h2>
          <p className="text-sm text-text-secondary">{t('deleteTripModal.subtitle')}</p>
        </div>
      </Modal.Title>
      <div className="flex flex-col gap-3">
        {errorDeleteTrip ? <Alert status="error">{t('deleteTripModal.error')}</Alert> : null}
      </div>
      <Modal.Nav>
        {({ handleClose }) => (
          <div className="flex flex-col gap-4 w-full">
            <LoadingButton
              loading={isLoadingAction}
              type="button"
              color="error"
              className="w-full"
              onClick={handleConfirm}
            >
              {t('button.delete_trip')}
            </LoadingButton>
            <Button type="button" color="ghost" className="text-primary" onClick={handleClose}>
              {t('button.cancel')}
            </Button>
          </div>
        )}
      </Modal.Nav>
    </Modal.Wrapper>
  );
};

export default DeleteTripModal;
